<template>
  <div class="help-root">

    <div class="help-intro">
      <span class="material-symbols-outlined help-hero-icon">allergies</span>
      <p>
        Registra y gestiona las alergias conocidas del paciente: tipo de alergia,
        severidad, fecha de diagnóstico y observaciones clínicas relevantes.
      </p>
    </div>

    <!-- Contexto de acceso -->
    <section class="help-section">
      <h3 class="help-section-title">
        <span class="material-symbols-outlined">alt_route</span>
        Formas de acceso
      </h3>
      <div class="help-cards">
        <div class="help-card" v-for="item in accesos" :key="item.titulo">
          <span class="material-symbols-outlined help-card-icon">{{ item.icon }}</span>
          <div>
            <p class="help-card-title">{{ item.titulo }}</p>
            <p class="help-card-desc">{{ item.desc }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- Datos de la persona -->
    <section class="help-section">
      <h3 class="help-section-title">
        <span class="material-symbols-outlined">person</span>
        Datos del paciente (solo lectura)
      </h3>
      <div class="help-cards">
        <div class="help-card" v-for="item in datosPersona" :key="item.titulo">
          <span class="material-symbols-outlined help-card-icon">{{ item.icon }}</span>
          <div>
            <p class="help-card-title">{{ item.titulo }}</p>
            <p class="help-card-desc">{{ item.desc }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- Campos del formulario -->
    <section class="help-section">
      <h3 class="help-section-title">
        <span class="material-symbols-outlined">edit_note</span>
        Campos del formulario
      </h3>
      <div class="help-cards">
        <div class="help-card" v-for="item in campos" :key="item.titulo">
          <span class="material-symbols-outlined help-card-icon">{{ item.icon }}</span>
          <div>
            <p class="help-card-title">{{ item.titulo }}</p>
            <p class="help-card-desc">{{ item.desc }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- Severidad -->
    <section class="help-section">
      <h3 class="help-section-title">
        <span class="material-symbols-outlined">info</span>
        Niveles de severidad
      </h3>
      <div class="help-estados">
        <span
          v-for="s in severidades"
          :key="s.label"
          class="help-estado-badge"
          :style="{ background: s.color, color: s.text }"
        >{{ s.label }}</span>
      </div>
    </section>

    <!-- Acciones -->
    <section class="help-section">
      <h3 class="help-section-title">
        <span class="material-symbols-outlined">touch_app</span>
        Acciones disponibles
      </h3>
      <div class="help-actions">
        <div class="help-action" v-for="a in acciones" :key="a.titulo">
          <span class="material-symbols-outlined help-action-icon" :style="{ color: a.color }">{{ a.icon }}</span>
          <div>
            <p class="help-card-title">{{ a.titulo }}</p>
            <p class="help-card-desc">{{ a.desc }}</p>
          </div>
        </div>
      </div>
    </section>

    <div class="help-tip">
      <span class="material-symbols-outlined">lightbulb</span>
      <p>
        El historial de alergias es visible desde cualquier atención del
        paciente. Mantenerlo actualizado es clave para evitar prescripciones
        contraindicadas.
      </p>
    </div>

  </div>
</template>

<script>
export default {
  name: "AyudaAlergiaPers",
  data() {
    return {
      accesos: [
        {
          icon: "medical_services",
          titulo: "Desde una atención",
          desc:   "Se accede desde el botón 'Alergias' en el panel lateral del formulario de atención. El breadcrumb muestra la atención de origen.",
        },
        {
          icon: "person",
          titulo: "Desde el perfil del paciente",
          desc:   "Se accede desde el formulario de la persona. El breadcrumb muestra el nombre del paciente.",
        },
      ],
      datosPersona: [
        {
          icon: "badge",
          titulo: "Nombre y documento",
          desc:   "Nombre completo y número de identificación del paciente. No son editables desde esta pantalla.",
        },
        {
          icon: "expand_more",
          titulo: "Más información",
          desc:   "Despliega datos adicionales: email, estado civil, localidad, teléfono, nombres, apellidos, fecha de nacimiento, sexo, dirección e IDs de auditoría.",
        },
      ],
      campos: [
        {
          icon: "allergies",
          titulo: "Alergia",
          desc:   "Selecciona el tipo de alergia del catálogo del sistema (medicamentos, alimentos, sustancias, etc.).",
        },
        {
          icon: "calendar_today",
          titulo: "Fecha de diagnóstico",
          desc:   "Fecha en la que se diagnosticó o identificó la alergia. Campo obligatorio.",
        },
        {
          icon: "warning",
          titulo: "Severidad",
          desc:   "Nivel de gravedad de la reacción alérgica: Leve, Moderada o Severa. Campo obligatorio.",
        },
        {
          icon: "text_snippet",
          titulo: "Observación",
          desc:   "Descripción adicional de la reacción alérgica, síntomas presentados o tratamiento indicado. Campo obligatorio.",
        },
        {
          icon: "event",
          titulo: "Fecha de registro",
          desc:   "Fecha en que se ingresó el registro en el sistema. Se asigna automáticamente y no es editable.",
        },
      ],
      severidades: [
        { label: "Leve",     color: "#d4edda", text: "#155724" },
        { label: "Moderada", color: "#fff3cd", text: "#856404" },
        { label: "Severa",   color: "#f8d7da", text: "#721c24" },
      ],
      acciones: [
        {
          icon:   "add_circle",
          color:  "#0059cc",
          titulo: "Nueva alergia",
          desc:   "Abre el formulario para registrar una nueva alergia del paciente. Requiere seleccionar el tipo, la severidad, la fecha de diagnóstico y una observación.",
        },
        {
          icon:   "edit",
          color:  "#198754",
          titulo: "Editar",
          desc:   "Abre el formulario con los datos de la alergia seleccionada para modificar cualquier campo.",
        },
        {
          icon:   "refresh",
          color:  "#6c757d",
          titulo: "Todos los registros",
          desc:   "Recarga la lista completa de alergias registradas para el paciente.",
        },
        {
          icon:   "download",
          color:  "#fd7e14",
          titulo: "Exportar Excel",
          desc:   "Descarga la lista de alergias del paciente en formato Excel para su archivo o análisis externo.",
        },
      ],
    };
  },
};
</script>

<style src="@/assets/css/ayuda.css" scoped></style>